import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { TransactionVendingClient } from '@frontend/transaction/api';
import { ExtendedVendingTransaction } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { useReplenishments } from './replenishments.controller';

const ID = 'replenishments';

const Replenishments = () => {
    const viewProps = useReplenishments();

    return (
        <div
            id={ID}
            key={ID}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader title={CommonMessage.OBJECTS.REPLENISHMENT.PLURAL} />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <ReactiveObjectList<ExtendedVendingTransaction>
                    dispatch={viewProps.dispatch}
                    fetch={(arg) => TransactionVendingClient.getReplenishments(arg)}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    activeFilter={{
                        searchKeys: ['product', 'user'],
                        filterKeys: ['spot_id', 'user_id', 'product_id', 'after', 'before'],
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        }),
                        displayName: new Map([
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    columns={{
                        columns: ['sku', 'ean', 'product', 'categories', 'quantity', 'creation_timestamp', 'spot', 'user'],
                        headerOverwrite: new Map([
                            [
                                'quantity',
                                <FormattedMessage
                                    key='replenishments.amount-filled'
                                    id='replenishments.amount-filled'
                                    defaultMessage='# filled'
                                />
                            ],
                            ['spot', CommonMessage.OBJECTS.SPOT.DEFAULT],
                            ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                            ['ean', CommonMessage.OBJECTS.PRODUCT.EAN]
                        ]),
                        customCells: new Map([
                            [
                                'categories',
                                (obj) => {
                                    if (obj.row.original.categories === null || obj.row.original.categories.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.categories[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.categories[0].entity_id}
                                                    entityType={EntityType.PRODUCT_CATEGORY}
                                                    display={obj.row.original.categories[0].name}
                                                    key={obj.row.original.categories[0].entity_id}
                                                    foreign={obj.row.original.categories[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.categories[0])}
                                                />
                                                {obj.row.original.categories.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.categories.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.PRODUCT_CATEGORY}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'product',
                                (obj) =>
                                    obj.row.original.product ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.product.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.product.name}
                                                entityType={obj.row.original.product.entity_type}
                                                id={obj.row.original.product.entity_id}
                                                key={obj.row.original.product.entity_id}
                                                foreign={obj.row.original.product.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'spot',
                                (obj) =>
                                    obj.row.original.spot ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.spot.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.spot.name}
                                                entityType={obj.row.original.spot.entity_type}
                                                id={obj.row.original.spot.entity_id}
                                                key={obj.row.original.spot.entity_id}
                                                foreign={obj.row.original.spot.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'user',
                                (obj) =>
                                    obj.row.original.user ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.user.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.user.name}
                                                entityType={obj.row.original.user.entity_type}
                                                id={obj.row.original.user.entity_id}
                                                key={obj.row.original.user.entity_id}
                                                foreign={obj.row.original.user.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ]
                        ])
                    }}
                />
            </div>
        </div>
    );
};

export default Replenishments;
